import { useRef } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { ArrowFullRightIcon, Button, StopwatchIcon } from '@motorway/mw-highway-code';

import { applyCypressData } from 'Utilities';
import useInView from 'Utilities/useInView';

import { useBreakpoint } from 'Components/misc/Breakpoints/Breakpoints';
import { ServiceFeeAppliesNotice } from 'Components/transactionFees/ServiceFeeAppliesNotice/ServiceFeeAppliesNotice';

import { Article, Aside, ContentLayout } from '../../common/ContentLayout/ContentLayout';

import { AcceptAndConfirmSale } from './AcceptAndConfirmSale/AcceptAndConfirmSale';
import { AcceptAndConfirmSaleSticky } from './AcceptAndConfirmSale/AcceptAndConfirmSaleSticky';
import { useAcceptAndConfirmSale } from './AcceptAndConfirmSale/useAcceptAndConfirmSale';
import { CompanyStats } from './CompanyStats/CompanyStats';
import { FAQs } from './FAQs/FAQs';
import { HeadingWithIcon } from './HeadingWithIcon/HeadingWithIcon';
import { PriceGraph } from './PriceGraph/PriceGraph';
import { WinningDealer } from './WinningDealer/WinningDealer';
import { formatTimeLeft } from './SellerConfirmationExperiment.helpers';
import texts from './SellerConfirmationExperiment.texts.json';
import { SellerConfirmationExperimentProps } from './SellerConfirmationExperiment.types';

import styles from './SellerConfirmationExperiment.scss';

const LocalT = new MDText(texts);

export const SellerConfirmationExperiment = (props: SellerConfirmationExperimentProps) => {
  const { serviceFee, serviceFeeTiers } = props.serviceFeeDetails || {};
  const formattedPrice = `£${props.offer?.closingBid?.value?.toLocaleString() ?? ''}`;

  const lowestOffer = props.offer?.bids?.map(({ value }) => value)?.sort((a, b) => (a > b ? 1 : -1))?.at(0);
  const dealer = props.offer?.closingBid?.dealerContact?.dealer?.name;

  const { loading, onSubmit } = useAcceptAndConfirmSale({
    errorProps: {
      content: <div>
        <p data-thc-style-override>{LocalT.translate('error.content')}</p>
        <Button label={LocalT.translate('error.cta')} onClick={() => globalThis.location.reload()} size="small" variant="secondary" />
      </div>,
      heading: LocalT.translate('error.heading'),
    },
    offer: props.offer,
    vehicleEnquiry: props.vehicleEnquiry,
  });

  const { minWidth: { breakpointMobile } } = useBreakpoint();
  const $wrapper = useRef<HTMLDivElement>(null);
  const isSectionVisible = useInView($wrapper, {
    rootMargin: '-24px',
  });

  const acceptAndConfirmProps = { ...props, loading, onSubmit };

  return <div className={cx(styles.wrapper, {
    [styles.belowReserve]: !!props.isBelowReserve,
  })}>
    <ContentLayout>
      <Article>
        <div ref={$wrapper} className={styles.primary}>
          <div className={styles.offers}>
            {lowestOffer && <div className={styles.lowest}>
              <div className={styles.label}>{LocalT.translate('primary.lowestOffer')}</div>
              <div className={styles.lowestOffer}>{`£${lowestOffer?.toLocaleString()}`}</div>
            </div>}

            <div className={styles.highest}>
              <div className={styles.label}>{LocalT.translate('primary.highestOffer')}</div>

              <PriceGraph className={styles.priceGraph} />

              <div className={styles.value} {...applyCypressData('acceptedSalePrice')}>{formattedPrice}</div>

              {props.isServiceFeeEnabled && serviceFeeTiers && <ServiceFeeAppliesNotice
                feeAmountGBP={serviceFee?.feeAmountGBP}
                label={LocalT.translate('primary.feeLabel') as string}
                serviceFeeTiers={serviceFeeTiers}
                variant="small"
              />}
            </div>
          </div>

          <HeadingWithIcon
            heading={LocalT.translate('depreciation.heading') as string}
            icon={<ArrowFullRightIcon data-depreciation-arrow />}
          />

          {props.timeLeft && <HeadingWithIcon
            heading={LocalT.translate('expiry.heading', { time: formatTimeLeft(props.timeLeft) }) as string}
            icon={<StopwatchIcon />}
          />}

          <AcceptAndConfirmSale className={styles.primaryAccept} {...acceptAndConfirmProps} />
        </div>
        {dealer && <WinningDealer dealer={dealer} />}
        <CompanyStats />
        <FAQs belowReserve={!!props.isBelowReserve} />
      </Article>
      <Aside>
        <p>Vehicle card</p>
      </Aside>
    </ContentLayout>
    <AcceptAndConfirmSaleSticky visible={
      !breakpointMobile || (!!$wrapper.current && !isSectionVisible)
    }>
      <AcceptAndConfirmSale {...acceptAndConfirmProps} />
    </AcceptAndConfirmSaleSticky>
  </div>;
};
